<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import HeaderSection from './components/HeaderSection.vue';
import BodySection from './components/BodySection.vue';
import FooterSection from './components/FooterSection.vue';
import ButtonsSection from './components/ButtonsSection.vue';
import VariableMappingSection from './components/VariableMappingSection.vue';
import {
  generateTemplateComponents,
  validateTemplateData,
} from 'dashboard/helper/templateHelper';
import {
  extractVariables,
  validateVariableMappings,
} from 'dashboard/helper/templateVariableHelper';

const { t } = useI18n();

const DEFAULT_PARAMETER_TYPE = 'positional';

const getDefaultTemplateData = () => ({
  header: {
    enabled: false,
    type: 'HEADER',
    format: 'TEXT',
    text: '',
    example: {
      header_text: [],
      header_text_named_params: [], // param_name, example object
    },
    media: {},
    error: '',
  },
  body: {
    type: 'BODY',
    text: '',
    example: {
      body_text: [],
      body_text_named_params: [],
    },
    error: '',
  },
  footer: {
    type: 'FOOTER',
    enabled: false,
    text: '',
  },
  buttons: [],
});

const parameterType = ref(DEFAULT_PARAMETER_TYPE); // 'positional' or 'named'
const isParameterTypeDropdownOpen = ref(false);

// TODO: For type constants use COMPONENT_TYPES from templateHelper
const templateData = ref(getDefaultTemplateData());
const variableMappings = ref({});
const showValidationErrors = ref(false); // Only show errors after user tries to save

// Extract variables from body text
const detectedVariables = computed(() => {
  return extractVariables(templateData.value.body.text);
});

// Validation results with errors
const validationResult = computed(() => {
  const templateValidation = validateTemplateData(
    templateData.value,
    parameterType.value
  );
  const variableValidation = validateVariableMappings(
    detectedVariables.value,
    variableMappings.value
  );

  return {
    isValid: templateValidation.isValid && variableValidation.isValid,
    errors: [...templateValidation.errors, ...variableValidation.errors],
    warnings: [...templateValidation.warnings, ...variableValidation.warnings],
  };
});

const isValidTemplate = computed(() => {
  return validationResult.value.isValid;
});

// Preview data for the preview panel
const previewData = computed(() => {
  return {
    header: templateData.value.header,
    body: templateData.value.body,
    footer: templateData.value.footer,
    buttons: templateData.value.buttons,
  };
});

const generateComponents = () => {
  // Show validation errors when user attempts to save
  showValidationErrors.value = true;

  // Check if valid before generating
  if (!isValidTemplate.value) {
    throw new Error('Please fix all validation errors before saving');
  }

  // Clone template data to avoid mutation
  const updatedTemplateData = JSON.parse(JSON.stringify(templateData.value));

  // Populate body examples from variable mappings
  if (detectedVariables.value.length > 0) {
    const exampleKey =
      parameterType.value === 'positional'
        ? 'body_text'
        : 'body_text_named_params';
    const examples = [];

    detectedVariables.value.forEach(variable => {
      const mapping = variableMappings.value[variable];

      if (!mapping) {
        throw new Error(
          `Variable {{${variable}}} must be configured before saving`
        );
      }

      // For static variables: use example_value
      // For dynamic variables: use fallback_value
      const exampleValue =
        mapping.type === 'static'
          ? mapping.example_value
          : mapping.fallback_value;

      if (!exampleValue || exampleValue.trim() === '') {
        const fieldName =
          mapping.type === 'static' ? 'example value' : 'fallback value';
        throw new Error(`Variable {{${variable}}} must have a ${fieldName}`);
      }

      if (parameterType.value === 'positional') {
        // Positional: array of values ["value1", "value2"]
        examples.push(exampleValue);
      } else {
        // Named: array of objects [{param_name: "name", example: "value"}]
        examples.push({
          param_name: variable.toString(),
          example: exampleValue,
        });
      }
    });

    // Ensure examples array is not empty
    if (examples.length === 0) {
      throw new Error('Examples array is empty but variables were detected');
    }

    // Set examples in the body component
    if (!updatedTemplateData.body.example) {
      updatedTemplateData.body.example = {};
    }
    updatedTemplateData.body.example[exampleKey] = examples;
  }

  const components = generateTemplateComponents(
    updatedTemplateData,
    parameterType.value
  );

  return components;
};

const getVariableMappings = () => {
  return variableMappings.value;
};

const getLocationMetadata = () => {
  // Extract location from header if it's a location template
  if (
    templateData.value.header?.enabled &&
    templateData.value.header?.format === 'LOCATION' &&
    templateData.value.header?.media?.location
  ) {
    return templateData.value.header.media.location;
  }
  return null;
};

const resetBuilderState = () => {
  parameterType.value = DEFAULT_PARAMETER_TYPE;
  templateData.value = getDefaultTemplateData();
  variableMappings.value = {};
};

const loadTemplateData = template => {
  if (!template) return;

  // Load parameter type
  parameterType.value = template.parameter_format || DEFAULT_PARAMETER_TYPE;

  // Parse components from template content
  const components = template.content?.components || [];

  components.forEach(component => {
    if (component.type === 'HEADER') {
      // Build media object if header has media format
      let mediaObject = {};
      if (component.format !== 'TEXT' && template.metadata?.media_url) {
        mediaObject = {
          fileUrl: template.metadata.media_url,
          fileName: template.metadata.media_format || component.format,
          contentType: template.metadata.media_format || component.format,
          // Add blobId to indicate media exists (for validation)
          blobId: template.metadata.media_blob_id,
        };
      } else if (
        component.format === 'LOCATION' &&
        template.metadata?.location
      ) {
        mediaObject = {
          location: template.metadata.location,
        };
      }

      templateData.value.header = {
        ...templateData.value.header,
        enabled: true,
        format: component.format,
        text: component.text || '',
        example: component.example || templateData.value.header.example,
        media: mediaObject,
      };
    } else if (component.type === 'BODY') {
      templateData.value.body = {
        ...templateData.value.body,
        text: component.text || '',
        example: component.example || templateData.value.body.example,
      };
    } else if (component.type === 'FOOTER') {
      templateData.value.footer = {
        ...templateData.value.footer,
        enabled: true,
        text: component.text || '',
      };
    } else if (component.type === 'BUTTONS') {
      templateData.value.buttons = component.buttons || [];
    }
  });

  // Load variable mappings from metadata
  if (template.metadata?.variable_mappings) {
    variableMappings.value = template.metadata.variable_mappings;
  }
};

defineExpose({
  generateComponents,
  getVariableMappings,
  getLocationMetadata,
  isValidTemplate,
  parameterType,
  resetBuilderState,
  loadTemplateData,
  previewData,
  variableMappings,
  validationResult,
});

const parameterTypesOptions = computed(() => [
  {
    action: 'parameterTypeSelect',
    value: 'positional',
    label: t('SETTINGS.TEMPLATES.BUILDER.PARAMETER_TYPES.OPTIONS.POSITIONAL'),
    isSelected: parameterType.value === 'positional',
  },
  {
    action: 'parameterTypeSelect',
    value: 'named',
    label: t('SETTINGS.TEMPLATES.BUILDER.PARAMETER_TYPES.OPTIONS.NAMED'),
    isSelected: parameterType.value === 'named',
  },
]);

const selectedParameterTypeLabel = computed(() => {
  const selected = parameterTypesOptions.value.find(
    option => option.value === parameterType.value
  );
  return selected ? selected.label : '';
});

const handleParameterTypeAction = ({ value }) => {
  parameterType.value = value;
  isParameterTypeDropdownOpen.value = false;
};

const toggleParameterTypeDropdown = () => {
  isParameterTypeDropdownOpen.value = !isParameterTypeDropdownOpen.value;
};

const closeParameterTypeDropdown = () => {
  isParameterTypeDropdownOpen.value = false;
};
</script>

<template>
  <div class="space-y-12">
    <!-- Parameter Type Selector -->
    <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
      <div class="flex items-center gap-3 mb-4">
        <h4 class="font-medium text-n-slate-12">
          {{ t('SETTINGS.TEMPLATES.BUILDER.PARAMETER_TYPES.TITLE') }}
        </h4>
      </div>

      <div class="space-y-3">
        <!-- Parameter Type Dropdown -->
        <div
          v-on-click-outside="closeParameterTypeDropdown"
          class="relative w-full"
        >
          <button
            type="button"
            class="flex items-center justify-between gap-2 w-full px-3 py-2 border border-n-blue-5 rounded-lg bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-5 focus:border-n-blue-5 transition-colors hover:bg-n-solid-2"
            @click="toggleParameterTypeDropdown"
          >
            <span class="truncate">{{ selectedParameterTypeLabel }}</span>
            <Icon
              icon="i-lucide-chevron-down"
              class="size-4 flex-shrink-0 transition-transform"
              :class="{ 'rotate-180': isParameterTypeDropdownOpen }"
            />
          </button>
          <DropdownMenu
            v-if="isParameterTypeDropdownOpen"
            :menu-items="parameterTypesOptions"
            class="absolute top-full mt-1 left-0 right-0 w-full z-50"
            @action="handleParameterTypeAction"
          />
        </div>
      </div>
    </div>

    <HeaderSection
      v-if="templateData.header"
      v-model="templateData.header"
      :parameter-type="parameterType"
    />
    <BodySection v-model="templateData.body" :parameter-type="parameterType" />

    <!-- Validation Errors Display - Only shown after user tries to save -->
    <div
      v-if="showValidationErrors && validationResult.errors.length > 0"
      class="bg-red-50 border border-red-200 rounded-lg p-4"
    >
      <div class="flex items-start gap-3">
        <span
          class="i-lucide-alert-circle size-5 text-red-600 flex-shrink-0 mt-0.5"
        />
        <div class="flex-1">
          <h4 class="text-sm font-medium text-red-900 mb-2">
            {{ t('WHATSAPP_TEMPLATES.VALIDATION.TITLE') }}
          </h4>
          <ul class="list-disc list-inside space-y-1 text-sm text-red-700">
            <li v-for="(error, index) in validationResult.errors" :key="index">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Variable Mapping Section - Appears right after Body -->
    <VariableMappingSection
      v-model="variableMappings"
      :variables="detectedVariables"
    />

    <FooterSection v-model="templateData.footer" />
    <ButtonsSection v-model="templateData.buttons" />
  </div>
</template>
